import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtEur, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { ORDER_FLOW, ORDER_STATUS_COLORS } from "../lib/ordersLib";
import { WindingSvg } from "../components/WindingPicker";
import { OrderConfirmationDialog } from "../components/OrderConfirmationDialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CommentsAudit } from "../components/CommentsAudit";
import { toast } from "sonner";
import { ArrowLeft, Save, Repeat, XCircle, ChevronRight, Plus, Trash2, FileDown, Mail } from "lucide-react";

const UNITS = ["1000 pcs", "pcs", "rolls", "m²"];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, canWrite } = useApp();
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [articles, setArticles] = useState([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [abOpen, setAbOpen] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => {
    setOrder(r.data);
    setLines(r.data.line_items || []);
    if (r.data.account_id) {
      api.get(`/articles?account_id=${r.data.account_id}&page_size=200`).then((a) => setArticles(a.data.items)).catch(() => {});
    }
  }).catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!order) return <p className="text-gray-400">{t("loading")}</p>;

  const editable = canWrite && ["Draft", "Entered"].includes(order.status);
  const terminal = ["Invoiced", "Cancelled"].includes(order.status);
  const flowIdx = ORDER_FLOW.indexOf(order.status);
  const nextStatus = flowIdx >= 0 && flowIdx < ORDER_FLOW.length - 1 ? ORDER_FLOW[flowIdx + 1] : null;

  const save = async (extra = {}) => {
    try {
      const r = await api.put(`/orders/${id}`, { ...order, line_items: lines, ...extra });
      setOrder(r.data); setLines(r.data.line_items || []);
      toast.success(t("save") + " ✓");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const setStatus = async (status, reason) => {
    try {
      const r = await api.post(`/orders/${id}/status`, { status, reason });
      setOrder(r.data);
      toast.success(`${t("ord_status_updated")}: ${status}`);
      setCancelOpen(false);
      if (status === "Confirmed" && canWrite) setAbOpen(true);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const downloadAb = async () => {
    try {
      const token = localStorage.getItem("lf_token");
      const res = await fetch(`${API}/orders/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${order.order_number}_AB.pdf`;
      a.click();
    } catch { toast.error("PDF error"); }
  };

  const reorder = async () => {
    try {
      const r = await api.post(`/orders/${id}/reorder`);
      toast.success(`${t("ord_reorder")} ✓ ${r.data.order_number}`);
      navigate(`/orders/${r.data.id}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const pickArticle = async (i, articleId) => {
    const art = articles.find((a) => a.id === articleId);
    if (!art) { setLine(i, { article_id: "", article_number: "" }); return; }
    const patch = { article_id: art.id, article_number: art.article_number, description: art.name,
                    unit: art.unit || "1000 pcs", winding_direction: art.winding_direction,
                    unit_price: art.price_per_1000 || 0 };
    try {
      const qty = lines[i]?.quantity || 0;
      const r = await api.get(`/conditions/resolve?account_id=${order.account_id}&article_id=${art.id}&qty=${qty}`);
      if (r.data.unit_price != null) patch.unit_price = r.data.unit_price;
      if (r.data.discount_pct != null) patch.discount_pct = r.data.discount_pct;
      if (r.data.condition_ids?.length) toast.info(t("ord_condition_applied"));
    } catch {}
    setLine(i, patch);
  };

  const lineTotal = (li) => {
    const qty = Number(li.quantity || 0), p = Number(li.unit_price || 0);
    const mode = li.pricing_mode || "per_1000";
    const base = mode === "per_unit" ? qty * p : mode === "flat" ? p : (qty / 1000) * p;
    return base * (1 - Number(li.discount_pct || 0) / 100);
  };
  const total = lines.reduce((s, li) => s + lineTotal(li), 0);

  const metaField = (labelKey, key, type = "text", alwaysEditable = false) => (
    <div>
      <Label className="text-xs text-gray-500">{t(labelKey)}</Label>
      {(editable || (alwaysEditable && canWrite)) ? (
        <Input type={type} data-testid={`order-field-${key}`} className="mt-1 h-9"
          value={order[key] || ""} onChange={(e) => setOrder((o) => ({ ...o, [key]: e.target.value }))} />
      ) : (
        <p className="mt-1 text-sm font-medium">{type === "date" ? fmtDate(order[key]) : (order[key] || "–")}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="order-detail-page">
      <button onClick={() => navigate("/orders")} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors duration-150" data-testid="back-to-orders">
        <ArrowLeft className="h-4 w-4" />{t("orders")}
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-mono text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }} data-testid="order-number">
              {order.order_number}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500">
              <button className="text-blue-600 hover:underline" onClick={() => navigate(`/accounts/${order.account_id}`)} data-testid="order-account-link">{order.account_name}</button>
              ·<Badge className={`${ORDER_STATUS_COLORS[order.status] || ""} hover:bg-inherit`} data-testid="order-status-badge">{order.status}</Badge>
              ·<span>{t("ord_source")}: {order.source}</span>
              {order.quote_number && (
                <>·<button className="text-blue-600 hover:underline" onClick={() => navigate(`/quotes/${order.quote_id}`)} data-testid="order-quote-link">
                  {t("ord_from_quote")} {order.quote_number}</button></>
              )}
              {order.reorder_of_number && <>·<span className="text-gray-400">{t("ord_reorder_of")} {order.reorder_of_number}</span></>}
            </div>
            {order.cancel_reason && <p className="text-xs text-red-500 mt-1">{t("ord_cancel_reason")}: {order.cancel_reason}</p>}
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{t("ord_total")}</p>
            <p className="text-2xl font-bold font-mono text-[#1B2A4A]" data-testid="order-total">{fmtEur(total, order.currency)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-5" data-testid="order-status-workflow">
          {ORDER_FLOW.map((s, i) => (
            <div key={s} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-gray-300" />}
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                s === order.status ? "bg-[#1B2A4A] text-white"
                : i < flowIdx ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}>
                {s}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {canWrite && nextStatus && !terminal && (
            <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={() => setStatus(nextStatus)} data-testid="order-advance-btn">
              {t("ord_advance")}: {nextStatus}
            </Button>
          )}
          {canWrite && !terminal && !["Shipped", "Delivered"].includes(order.status) && (
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelOpen(true)} data-testid="order-cancel-btn">
              <XCircle className="h-3.5 w-3.5 mr-1" />{t("ord_cancel")}
            </Button>
          )}
          {canWrite && (
            <Button size="sm" variant="outline" onClick={reorder} data-testid="order-reorder-btn">
              <Repeat className="h-3.5 w-3.5 mr-1" />{t("ord_reorder")}
            </Button>
          )}
          {["Confirmed", "In Production", "Shipped", "Delivered", "Invoiced"].includes(order.status) && (
            <>
              <Button size="sm" variant="outline" onClick={downloadAb} data-testid="order-ab-pdf-btn">
                <FileDown className="h-3.5 w-3.5 mr-1" />{t("ord_ab_pdf")}
              </Button>
              {canWrite && (
                <Button size="sm" className="bg-[#F5A623] hover:bg-[#e09612] text-[#1B2A4A]" onClick={() => setAbOpen(true)} data-testid="order-send-ab-btn">
                  <Mail className="h-3.5 w-3.5 mr-1" />{t("ord_send_ab")}
                </Button>
              )}
            </>
          )}
        </div>
        {order.confirmation_sent_to && (
          <p className="text-xs text-gray-500 mt-2" data-testid="order-ab-last-sent">
            {t("ord_ab_last_sent")}: {order.confirmation_sent_to} · {fmtDate(order.confirmation_sent_at)}
          </p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100">
          {metaField("ord_po", "po_number")}
          {metaField("ord_requested", "requested_date", "date")}
          {metaField("incoterm", "incoterm")}
          {metaField("payment_terms", "payment_terms")}
          {metaField("ord_erp", "erp_number", "text", true)}
          <div>
            <Label className="text-xs text-gray-500">{t("ord_date")}</Label>
            <p className="mt-1 text-sm font-medium">{fmtDate(order.order_date)}</p>
          </div>
          {order.confirmed_at && (
            <div>
              <Label className="text-xs text-gray-500">{t("ord_confirmed_at")}</Label>
              <p className="mt-1 text-sm font-medium">{fmtDate(order.confirmed_at)}</p>
            </div>
          )}
          {order.owner_name && (
            <div>
              <Label className="text-xs text-gray-500">{t("owner")}</Label>
              <p className="mt-1 text-sm font-medium">{order.owner_name}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="order-lines-panel">
        <h2 className="text-base font-semibold mb-3">{t("ord_lines")}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="px-2 py-2 w-8">#</th>
                <th className="px-2 py-2 min-w-[180px]">{t("article_number")}</th>
                <th className="px-2 py-2 min-w-[180px]">{t("ord_description")}</th>
                <th className="px-2 py-2 w-28 text-right">{t("quantity")}</th>
                <th className="px-2 py-2 w-24">{t("unit")}</th>
                <th className="px-2 py-2 w-28 text-right">{t("unit_price")}</th>
                <th className="px-2 py-2 w-20 text-right">{t("cond_discount")}</th>
                <th className="px-2 py-2 w-16 text-center">{t("art_winding")}</th>
                <th className="px-2 py-2 w-28 text-right">{t("line_total")}</th>
                {editable && <th className="px-2 py-2 w-10" />}
              </tr>
            </thead>
            <tbody>
              {lines.map((li, i) => (
                <tr key={i} className="border-b border-gray-50" data-testid={`order-line-${i}`}>
                  <td className="px-2 py-2 text-gray-400 font-mono">{i + 1}</td>
                  <td className="px-2 py-2">
                    {editable ? (
                      <select data-testid={`order-line-article-${i}`} className="w-full h-8 rounded border border-gray-200 bg-white px-1 text-xs"
                        value={li.article_id || ""} onChange={(e) => pickArticle(i, e.target.value)}>
                        <option value="">{t("ord_select_article")}</option>
                        {articles.map((a) => <option key={a.id} value={a.id}>{`${a.article_number} – ${a.name}`}</option>)}
                      </select>
                    ) : <span className="font-mono text-xs">{li.article_number || "–"}</span>}
                  </td>
                  <td className="px-2 py-2">
                    {editable ? (
                      <Input className="h-8 text-xs" value={li.description || ""} onChange={(e) => setLine(i, { description: e.target.value })} />
                    ) : (li.description || "–")}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {editable ? (
                      <Input type="number" className="h-8 text-xs text-right" data-testid={`order-line-qty-${i}`}
                        value={li.quantity ?? ""} onChange={(e) => setLine(i, { quantity: e.target.value })} />
                    ) : <span className="font-mono">{Number(li.quantity || 0).toLocaleString("de-DE")}</span>}
                  </td>
                  <td className="px-2 py-2">
                    {editable ? (
                      <select className="w-full h-8 rounded border border-gray-200 bg-white px-1 text-xs"
                        value={li.unit || "1000 pcs"} onChange={(e) => setLine(i, { unit: e.target.value })}>
                        {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    ) : li.unit}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {editable ? (
                      <Input type="number" step="0.01" className="h-8 text-xs text-right" data-testid={`order-line-price-${i}`}
                        value={li.unit_price ?? ""} onChange={(e) => setLine(i, { unit_price: e.target.value })} />
                    ) : <span className="font-mono">{fmtEur(li.unit_price, order.currency)}</span>}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {editable ? (
                      <Input type="number" step="0.5" className="h-8 text-xs text-right"
                        value={li.discount_pct ?? ""} onChange={(e) => setLine(i, { discount_pct: e.target.value })} />
                    ) : <span className="font-mono">{li.discount_pct || 0}%</span>}
                  </td>
                  <td className="px-2 py-2">
                    {li.winding_direction ? (
                      <div className="w-12 mx-auto" title={`${t("art_winding")} ${li.winding_direction}`}>
                        <WindingSvg dir={Number(li.winding_direction)} className="w-full h-8" />
                        <div className="text-[10px] text-center text-gray-500 font-semibold -mt-0.5">{li.winding_direction}</div>
                      </div>
                    ) : <span className="block text-center text-gray-300">–</span>}
                  </td>
                  <td className="px-2 py-2 text-right font-mono font-medium" data-testid={`order-line-total-${i}`}>{fmtEur(lineTotal(li), order.currency)}</td>
                  {editable && (
                    <td className="px-2 py-2">
                      <button onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-500 transition-colors" data-testid={`order-line-delete-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={10} className="px-2 py-8 text-center text-gray-400">{t("empty_title")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4">
          {editable ? (
            <Button size="sm" variant="outline" onClick={() => setLines((ls) => [...ls, { quantity: 25000, unit: "1000 pcs", pricing_mode: "per_1000", discount_pct: 0 }])} data-testid="order-add-line-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />{t("ord_add_line")}
            </Button>
          ) : <span />}
          {(editable || canWrite) && (
            <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={() => save()} data-testid="order-save-btn">
              <Save className="h-4 w-4 mr-1.5" />{t("save")}
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <CommentsAudit module="orders" id={id} />
      </div>

      <OrderConfirmationDialog open={abOpen} onOpenChange={setAbOpen} order={order} onSent={load} />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("ord_cancel")}</DialogTitle>
            <DialogDescription>{order.order_number} · {order.account_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("ord_cancel_reason")}</Label>
              <Input data-testid="order-cancel-reason" className="mt-1" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCancelOpen(false)}>{t("cancel")}</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setStatus("Cancelled", cancelReason)} data-testid="order-cancel-confirm">
                {t("ord_cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
